import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LocationSelect } from '@/components/LocationSelect';
import { CANADIAN_LOCATIONS, normalizeLocationSearch } from '@/lib/canadianLocations';
import { GENDER_OPTIONS, HYPOALLERGENIC_OPTIONS } from '@/lib/listingAttributes';
import { ArrowLeft, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

function getLocationValue(provinceName, cityName) {
  if (provinceName && cityName) return `${cityName}, ${provinceName}`;
  return provinceName || '';
}

function parseListingLocation(value) {
  const locationText = typeof value === 'object'
    ? [value?.text, value?.city, value?.province, value?.state].filter(Boolean).join(' ')
    : String(value || '');
  const normalized = normalizeLocationSearch(locationText);
  if (!normalized) return { province: '', city: '' };

  for (const provinceInfo of CANADIAN_LOCATIONS) {
    const province = normalizeLocationSearch(provinceInfo.province);
    const abbreviation = normalizeLocationSearch(provinceInfo.abbreviation);
    const matchedCity = provinceInfo.cities.find(city => normalized.includes(normalizeLocationSearch(city)));

    if (matchedCity && (normalized.includes(province) || normalized.includes(abbreviation))) {
      return { province: provinceInfo.province, city: matchedCity };
    }
    if (matchedCity && !normalized.includes(province) && !normalized.includes(abbreviation)) {
      return { province: provinceInfo.province, city: matchedCity };
    }
    if (normalized === province || normalized === abbreviation || normalized.includes(province)) {
      return { province: provinceInfo.province, city: '' };
    }
  }

  return { province: '', city: '' };
}

export default function CreateListingPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [breedOptions, setBreedOptions] = useState([]);
  const [province, setProvince] = useState('');
  const [city, setCity] = useState('');
  const [form, setForm] = useState({
    title: '', breed: '', age: '', price: '', location: '',
    gender: 'not_specified', hypoallergenic: 'not_specified',
    description: '', size: 'medium', vaccination_status: 'unknown', breeder_info: '',
    contact_email: '', contact_phone: ''
  });

  useEffect(() => {
    api.getBreedNames().then(r => setBreedOptions([...(r.data.names || []), 'Other'])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit && user?.email) {
      setForm(f => f.contact_email ? f : { ...f, contact_email: user.email });
    }
  }, [isEdit, user?.email]);

  useEffect(() => {
    if (isEdit) {
      api.getListing(id).then(r => {
        const l = r.data;
        const parsedLocation = parseListingLocation(l.location || '');
        setProvince(parsedLocation.province);
        setCity(parsedLocation.city);
        setForm({
          title: l.title || '', breed: l.breed || '', age: l.age || '',
          price: String(l.price || ''), location: getLocationValue(parsedLocation.province, parsedLocation.city) || l.location || '',
          gender: l.gender || 'not_specified',
          hypoallergenic: l.hypoallergenic || 'not_specified',
          description: l.description || '', size: l.size || 'medium',
          vaccination_status: l.vaccination_status || 'unknown',
          breeder_info: l.breeder_info || '',
          contact_email: l.contact_email || l.user_email || '',
          contact_phone: l.contact_phone || ''
        });
        setImages(l.images || []);
      }).catch(() => navigate('/dashboard'));
    }
  }, [id, isEdit, navigate]);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploadPromises = files.map(file => api.uploadFile(file));
      const results = await Promise.all(uploadPromises);
      const paths = results.map(r => r.data.path);
      setImages(prev => [...prev, ...paths]);
      toast.success(`${files.length} image(s) uploaded`);
    } catch {
      toast.error('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const updateLocationSelection = (nextProvince, nextCity = '') => {
    setProvince(nextProvince);
    setCity(nextCity);
    setForm(f => ({ ...f, location: getLocationValue(nextProvince, nextCity) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!province) {
      toast.error('Please select a province or city');
      return;
    }
    setLoading(true);
    try {
      const submittedLocation = getLocationValue(province, city) || form.location;
      const payload = {
        ...form,
        price: parseFloat(form.price),
        location: submittedLocation,
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
        images
      };
      if (isEdit) {
        await api.updateListing(id, payload);
        toast.success('Listing updated!');
      } else {
        await api.createListing(payload);
        toast.success('Listing created!');
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save listing');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in" data-testid="create-listing-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#5C636A] hover:text-[#1A1D20] mb-6 transition-colors" data-testid="create-back-button">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <h1 className="font-['Nunito'] font-extrabold text-3xl text-[#1A1D20] tracking-tight mb-8">
        {isEdit ? 'Edit Listing' : 'Create New Listing'}
      </h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Images */}
        <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <h2 className="font-['Nunito'] font-bold text-lg text-[#1A1D20] mb-4">Photos</h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4">
            {images.map((path, i) => (
              <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100">
                <img src={api.getFileUrl(path)} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                  data-testid={`remove-image-${i}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <label className="aspect-square rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center cursor-pointer hover:border-[#4A90E2] hover:bg-blue-50/30 transition-colors" data-testid="upload-image-trigger">
              <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" />
              {uploading ? (
                <div className="w-5 h-5 border-2 border-[#4A90E2] border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Upload className="w-5 h-5 text-[#5C636A] mb-1" />
                  <span className="text-xs text-[#5C636A]">Add Photos</span>
                </>
              )}
            </label>
          </div>
          {images.length === 0 && (
            <p className="text-xs text-[#5C636A]">Upload at least one photo of your dog</p>
          )}
        </div>

        {/* Basic Info */}
        <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <h2 className="font-['Nunito'] font-bold text-lg text-[#1A1D20] mb-4">Basic Info</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-[#5C636A]">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => updateField('title', e.target.value)}
                placeholder="e.g., Adorable Golden Retriever Puppy"
                className="mt-1 rounded-xl bg-gray-50"
                required
                data-testid="listing-title-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-[#5C636A]">Breed</Label>
                <Select value={form.breed} onValueChange={(v) => updateField('breed', v)}>
                  <SelectTrigger className="mt-1 rounded-xl bg-gray-50" data-testid="listing-breed-select">
                    <SelectValue placeholder="Select breed" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {breedOptions.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-[#5C636A]">Age</Label>
                <Input
                  value={form.age}
                  onChange={(e) => updateField('age', e.target.value)}
                  placeholder="e.g., 3 months"
                  className="mt-1 rounded-xl bg-gray-50"
                  required
                  data-testid="listing-age-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-[#5C636A]">Price ($)</Label>
                <Input
                  type="number"
                  value={form.price}
                  onChange={(e) => updateField('price', e.target.value)}
                  placeholder="e.g., 1500"
                  className="mt-1 rounded-xl bg-gray-50"
                  required
                  min="0"
                  data-testid="listing-price-input"
                />
              </div>
              <div>
                <Label className="text-xs font-medium text-[#5C636A]">Location</Label>
                <div className="mt-1">
                  <LocationSelect
                    value={{ province, city }}
                    onChange={updateLocationSelection}
                    placeholder="Select province or city"
                    testId="listing-location-select"
                  />
                </div>
                <p className="mt-1.5 text-xs text-[#5C636A]">
                  Saved location: {getLocationValue(province, city) || 'Select a province or city'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-[#5C636A]">Gender</Label>
                <Select value={form.gender} onValueChange={(v) => updateField('gender', v)}>
                  <SelectTrigger className="mt-1 rounded-xl bg-gray-50" data-testid="listing-gender-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-[#5C636A]">Hypoallergenic</Label>
                <Select value={form.hypoallergenic} onValueChange={(v) => updateField('hypoallergenic', v)}>
                  <SelectTrigger className="mt-1 rounded-xl bg-gray-50" data-testid="listing-hypoallergenic-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HYPOALLERGENIC_OPTIONS.map(option => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium text-[#5C636A]">Size</Label>
                <Select value={form.size} onValueChange={(v) => updateField('size', v)}>
                  <SelectTrigger className="mt-1 rounded-xl bg-gray-50" data-testid="listing-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Small</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="large">Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium text-[#5C636A]">Vaccination Status</Label>
                <Select value={form.vaccination_status} onValueChange={(v) => updateField('vaccination_status', v)}>
                  <SelectTrigger className="mt-1 rounded-xl bg-gray-50" data-testid="listing-vaccination-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vaccinated">Vaccinated</SelectItem>
                    <SelectItem value="partial">Partially Vaccinated</SelectItem>
                    <SelectItem value="not_vaccinated">Not Vaccinated</SelectItem>
                    <SelectItem value="unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <h2 className="font-['Nunito'] font-bold text-lg text-[#1A1D20] mb-4">Details</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium text-[#5C636A]">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => updateField('description', e.target.value)}
                placeholder="Describe your dog: personality, health, temperament..."
                className="mt-1 rounded-xl bg-gray-50 min-h-[120px]"
                required
                data-testid="listing-description-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#5C636A]">Breeder Info (optional)</Label>
              <Textarea
                value={form.breeder_info}
                onChange={(e) => updateField('breeder_info', e.target.value)}
                placeholder="Breeder name, certifications, experience..."
                className="mt-1 rounded-xl bg-gray-50 min-h-[80px]"
                data-testid="listing-breeder-info-input"
              />
            </div>
          </div>
        </div>

        {/* Seller Contact */}
        <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <h2 className="font-['Nunito'] font-bold text-lg text-[#1A1D20] mb-2">Seller Contact</h2>
          <p className="text-xs text-[#5C636A] mb-4">
            Buyers will use these details from your listing to email or call you directly.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium text-[#5C636A]">Contact Email</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) => updateField('contact_email', e.target.value)}
                placeholder={user?.email || 'seller@example.com'}
                className="mt-1 rounded-xl bg-gray-50"
                data-testid="listing-contact-email-input"
              />
              <p className="text-[11px] text-[#5C636A] mt-1">Leave as your account email or enter a preferred seller email.</p>
            </div>
            <div>
              <Label className="text-xs font-medium text-[#5C636A]">Phone Number (optional)</Label>
              <Input
                type="tel"
                value={form.contact_phone}
                onChange={(e) => updateField('contact_phone', e.target.value)}
                placeholder="e.g., +1 555 123 4567"
                className="mt-1 rounded-xl bg-gray-50"
                data-testid="listing-contact-phone-input"
              />
              <p className="text-[11px] text-[#5C636A] mt-1">If provided, buyers will see a Call Seller button.</p>
            </div>
          </div>
        </div>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="cancel-listing-button">
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading || uploading}
            className="rounded-full bg-[#F5A623] hover:bg-[#e09515] text-white font-bold px-8 shadow-[0_4px_14px_rgb(245,166,35,0.4)] cta-button"
            data-testid="submit-listing-button"
          >
            {loading ? 'Saving...' : isEdit ? 'Update Listing' : 'Post Listing'}
          </Button>
        </div>
      </form>
    </div>
  );
}
